import { gql, MutationResult, useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { MutationReturnType } from 'basics/types/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const setTacticStatusToCancelledMutationCall = gql`
  mutation setTacticStatusToCancelled($id: String!, $sendToVeeva: Boolean!) {
    setTacticStatusToCancelled(id: $id, sendToVeeva: $sendToVeeva)
  }
`;

type ApiResultType = {
  setTacticStatusToCancelled: boolean;
};

type UseSetTacticStatusToCancelledMutation = [
  (
    id: string,
    sendToVeeva?: boolean,
  ) => Promise<MutationReturnType<boolean>>,
  MutationResult<ApiResultType>,
];

export const useSetTacticStatusToCancelledMutation = (): UseSetTacticStatusToCancelledMutation => {
  const [setTacticStatusToCancelled, state] = useMutation<ApiResultType>(setTacticStatusToCancelledMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });
  const flags = useFlags();

  const executeMutation = useCallback(async (id, sendToVeeva = true) => {
    try {
      const { data } = await setTacticStatusToCancelled({
        variables: {
          id,
          sendToVeeva: flags.mayaEnableVeevaBiogenlinc1816 && sendToVeeva,
        },
      });
      return { data: !!data?.setTacticStatusToCancelled, error: null };
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'setTacticStatusToCancelled.ts - useSetTacticStatusToCancelledMutation',
        });

        return { data: false, error: customContext.message as string };
      }

      return { data: false, error: null };
    }
  }, [setTacticStatusToCancelled, flags.mayaEnableVeevaBiogenlinc1816]);

  return [executeMutation, state];
};
